import * as React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { reduxForm, Field, FieldArray } from 'redux-form/immutable';
import classnames from 'classnames';
import Immutable from 'immutable';
import { filter, map, set, range, slice, join, cloneDeep } from 'lodash';
import moment from 'moment-timezone';

import { withStyles } from '@material-ui/core/styles';
import { AddCircle as AddCircleIcon, RemoveCircle as RemoveCircleIcon } from '@material-ui/icons';

import { DialogHeader, Button, Dialog, DialogActions, DialogContent, IconButton } from 'embeds/components';
import FormTimePicker from 'embeds/components/ReduxForm/FormTimePicker';
import { createValidator, validateAvailabilityBlocksTimeRange } from 'embeds/utils/validation';

import { makeSelectVisible } from './selectors';
import {
  closeDialogAvailabilityBlocks,
  changeAvailabilityBlocks,
  changeAvailabilityBlocksAppointmentType,
} from './actions';
import { DIALOG_AVAILABILITY_BLOCKS_FORM } from './constants';

import styles from './styles';

export const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const validate = createValidator(
  range(6).reduce((acc, curr) => {
    acc[`availabilityBlocks[${curr}]`] = {
      type: 'collection',
      fields: {
        endTime: validateAvailabilityBlocksTimeRange('endTime'),
        startTime: validateAvailabilityBlocksTimeRange('startTime'),
      },
    };
    return acc;
  }, {}),
);

class DialogAvailabilityBlocks extends React.PureComponent {
  componentWillReceiveProps(nextProps) {
    const { availabilityBlocks, visible, initialize } = nextProps;

    if (visible && !this.props.visible) {
      const availabilityBlocksObject = this.createAvailabilityBlocksObject(availabilityBlocks);
      const formattedAvailabilityObject = map(availabilityBlocksObject.availabilityBlocks, (dayAvailability) =>
        dayAvailability.map((timeAvailability) => {
          let { startTime, endTime } = timeAvailability;
          if (typeof startTime === 'number' && typeof endTime === 'number') {
            startTime = startTime.toString();

            if (startTime.length === 3) {
              startTime = `0${startTime}`;
            }
            // any date so we can format the time with moment, we just need the time.
            startTime = moment()
              .set({ hour: join(slice(startTime, 0, 2), ''), minutes: join(slice(startTime, 2, 4), '') })
              .format();

            endTime = endTime.toString();

            if (endTime.length === 3) {
              endTime = `0${endTime}`;
            }
            // any date so we can format the time with moment, we just need the time.
            endTime = moment()
              .set({ hour: join(slice(endTime, 0, 2), ''), minutes: join(slice(endTime, 2, 4), '') })
              .format();

            return {
              dayOfWeek: timeAvailability.dayOfWeek, // day of week always exist on this object as a number.
              startTime,
              endTime,
            };
          }
          return timeAvailability;
        }),
      );
      initialize({ availabilityBlocks: formattedAvailabilityObject });
    }
  }

  createAvailabilityBlocksObject(availabilityBlocks) {
    const object = weekDays.map((day, index) => {
      const itemsByDay = filter(availabilityBlocks, (item) => item.dayOfWeek === index);
      if (itemsByDay.length > 0) return itemsByDay;

      return [{ dayOfWeek: index, startTime: null, endTime: null }];
    });

    return { availabilityBlocks: object };
  }

  handleAddItem = (fields, index, day) => {
    const { insert } = fields;

    insert(
      index + 1,
      Immutable.Map({
        dayOfWeek: day,
        startTime: null,
        endTime: null,
      }),
    );
  };

  handleRemoveItem = (fields, index, day) => {
    const { remove, push, length } = fields;
    remove(index);
    if (length <= 1) {
      push(
        Immutable.Map({
          dayOfWeek: day,
          startTime: null,
          endTime: null,
        }),
      );
    }
  };

  handleSubmit = (body) => {
    const { dispatchSaveAvailabilityBlocks, source, onCustomChange } = this.props;
    const availabilityBlocks = [];

    map(body.toJS(), (items) =>
      map(items, (item) =>
        map(item, (child) => {
          const dates = { ...child };

          if (
            child.startTime &&
            child.endTime &&
            moment(child.startTime).isValid() &&
            moment(child.endTime).isValid()
          ) {
            const { startTime, endTime } = child;

            set(dates, 'startTime', startTime);
            set(dates, 'endTime', endTime);

            availabilityBlocks.push(dates);
          }
        }),
      ),
    );

    if (onCustomChange) {
      const customAvailabilityBlocks = cloneDeep(availabilityBlocks);
      customAvailabilityBlocks.forEach((block) => {
        const startDate = new Date(block.startTime);
        const endDate = new Date(block.endTime);
        // eslint-disable-next-line
        block.startTime = startDate.getHours() * 100 + startDate.getMinutes();
        // eslint-disable-next-line
        block.endTime = endDate.getHours() * 100 + endDate.getMinutes();
      });
      onCustomChange(customAvailabilityBlocks);
    }
    dispatchSaveAvailabilityBlocks(availabilityBlocks, source);
  };

  renderFooter() {
    const { dispatchCloseDialogAvailabilityBlocks, handleSubmit, pristine } = this.props;

    return (
      <DialogActions>
        <Button color="primary" onClick={dispatchCloseDialogAvailabilityBlocks} variant="text">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={pristine}
          form={DIALOG_AVAILABILITY_BLOCKS_FORM}
          onClick={handleSubmit(this.handleSubmit)}
          variant="contained"
          data-lh-id="availability-done-editing-btn"
        >
          Done Editing
        </Button>
      </DialogActions>
    );
  }

  renderTimeField(item, name) {
    const { classes } = this.props;

    return (
      <div className={classes.field}>
        <Field
          component={FormTimePicker}
          emptyLabel="none"
          fullWidth
          invalidLabel="none"
          label=""
          name={`${item}.${name}`}
        />
      </div>
    );
  }

  renderRemoveButton(fields, index, day, isFieldPopulated) {
    const { classes } = this.props;

    const hide = Boolean(fields.length === 1 && !isFieldPopulated);

    return (
      <div className={classes.buttonsWrapper}>
        <IconButton
          className={classnames(classes.closeButton, { [classes.hide]: hide })}
          onClick={() => this.handleRemoveItem(fields, index, day)}
          size="small"
        >
          <RemoveCircleIcon className={classes.removeCircleIcon} />
        </IconButton>
      </div>
    );
  }

  renderAddButton(fields, index, day) {
    const { classes } = this.props;

    return (
      <div className={classes.buttonsWrapper}>
        <IconButton onClick={() => this.handleAddItem(fields, index, day)} size="small">
          <AddCircleIcon className={classes.addIcon} />
        </IconButton>
      </div>
    );
  }

  renderDayOfWeek(item) {
    return <Field component="input" label="" name={`${item}.dayOfWeek`} type="hidden" />;
  }

  renderDayName(show, day) {
    const { classes } = this.props;

    return <span className={classes.dayName}>{show ? day : ''}</span>;
  }

  renderItem(fields, item, index, date) {
    const { classes } = this.props;
    const row = fields && fields.getAll().toJS()[index];
    const startTime = row && row.startTime;
    const endTime = row && row.endTime;
    const isFieldPopulated = Boolean(startTime) || Boolean(endTime);

    return (
      <div className={classes.fieldsTimesRow} key={index}>
        <div>
          {this.renderDayName(index === 0, date.day)}
          {this.renderDayOfWeek(item)}
        </div>
        <div className={classes.times}>
          {this.renderTimeField(item, 'startTime')}
          <span className={classes.to}>to</span>
          {this.renderTimeField(item, 'endTime')}
          {this.renderRemoveButton(fields, index, date.index, isFieldPopulated)}
          {this.renderAddButton(fields, index, date.index)}
        </div>
      </div>
    );
  }

  renderItems = ({ fields, date }) => {
    const { classes } = this.props;

    return (
      <div className={classes.fieldsDaysRow}>
        {fields.map((item, index) => this.renderItem(fields, item, index, date))}
      </div>
    );
  };

  renderContent() {
    const { classes, handleSubmit } = this.props;

    return (
      <DialogContent className={classes.content}>
        <form id={DIALOG_AVAILABILITY_BLOCKS_FORM} onSubmit={handleSubmit(this.handleSubmit)}>
          {map(weekDays, (day, index) => (
            <FieldArray
              key={`availabilityBlocks_${index}`}
              name={`availabilityBlocks[${index}]`}
              component={this.renderItems}
              date={{ day, index }}
            />
          ))}
        </form>
      </DialogContent>
    );
  }

  render() {
    const { dispatchCloseDialogAvailabilityBlocks, visible } = this.props;

    return (
      <Dialog fullWidth onClose={dispatchCloseDialogAvailabilityBlocks} open={visible}>
        <DialogHeader onRequestClose={dispatchCloseDialogAvailabilityBlocks} title="Edit reserved times" />
        {this.renderContent()}
        {this.renderFooter()}
      </Dialog>
    );
  }
}

DialogAvailabilityBlocks.propTypes = {
  availabilityBlocks: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  classes: PropTypes.object.isRequired,
  dispatchCloseDialogAvailabilityBlocks: PropTypes.func.isRequired,
  dispatchSaveAvailabilityBlocks: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  source: PropTypes.string.isRequired,
  visible: PropTypes.bool,
};

DialogAvailabilityBlocks.defaultProps = {
  availabilityBlocks: false,
  visible: false,
};

const mapStateToProps = createStructuredSelector({
  visible: makeSelectVisible(),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchCloseDialogAvailabilityBlocks: () => {
    dispatch(closeDialogAvailabilityBlocks());
  },
  dispatchSaveAvailabilityBlocks: (availabilityBlocks, source) => {
    if (source === 'provider') {
      dispatch(changeAvailabilityBlocks(availabilityBlocks));
    } else {
      dispatch(changeAvailabilityBlocksAppointmentType(availabilityBlocks));
    }

    dispatch(closeDialogAvailabilityBlocks());
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withForm = reduxForm({
  form: DIALOG_AVAILABILITY_BLOCKS_FORM,
  validate,
});

export default compose(withConnect, withForm, withStyles(styles))(DialogAvailabilityBlocks);
